import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Paper
} from '@mui/material';
import { Delete, Warning } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { bulkDeleteModalStyles, expandableChipStyles } from '../../shared/globalSX';
import { BULK_EDIT_MODAL } from '../../shared/appConstants';
import './BulkDeleteModal.css';

const BulkDeleteModal = ({ open, onClose, onConfirm, selectedTasks = [], loading = false, totalCount = 0 }) => {
  const { t } = useTranslation();
  const [visibleCount, setVisibleCount] = useState(BULK_EDIT_MODAL.INITIAL_VISIBLE_COUNT);
  const dialogSx = {
    '& .MuiDialog-container': {
      alignItems: 'center',
      justifyContent: 'center'
    },
    '& .MuiDialog-paper': {
      margin: 0,
      position: 'relative'
    }
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      disableScrollLock
      container={typeof document !== 'undefined' ? document.body : undefined}
      sx={dialogSx}
      className="bulk-delete-modal"
      PaperProps={{
        sx: bulkDeleteModalStyles.dialogPaper
      }}
    >
      <DialogTitle sx={bulkDeleteModalStyles.dialogTitle}>
        <Delete sx={bulkDeleteModalStyles.deleteIcon} />
        <Box>
          <Typography variant="h5" fontWeight="700">
            {t('dashboard.bulkActions.deleteModal.title')}
          </Typography>
          <Typography variant="body2">
            {t('dashboard.bulkActions.deleteModal.subtitle')}
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box className="content-container">
          {/* Warning */}
          <Paper className="warning-paper" elevation={0}>
            <Warning className="warning-icon" />
            <Box>
              <Typography variant="subtitle2" className="warning-title">
                {t('dashboard.bulkActions.deleteModal.warningTitle')}
              </Typography>
              <Typography variant="body2" className="warning-text">
                {t('dashboard.bulkActions.deleteModal.warningMessage', { count: selectedTasks.length, total: totalCount })}
              </Typography>
            </Box>
          </Paper>

          {/* Selected Tasks */}
          <Box>
            <Typography variant="subtitle2" className="tasks-title">
              {t('dashboard.bulkActions.deleteModal.tasksToDelete')}
            </Typography>
            <Box className="tasks-container" sx={{ maxHeight: 300, overflow: 'auto' }}>
              {selectedTasks.slice(0, visibleCount).map(task => (
                <Chip
                  key={task.id}
                  label={task.title}
                  size="small"
                  className="task-chip"
                />
              ))}
              {selectedTasks.length > visibleCount && (
                <Chip
                  label={`+ ${selectedTasks.length - visibleCount} more`}
                  size="small"
                  variant="outlined"
                  onClick={() => setVisibleCount(prev => Math.min(prev + BULK_EDIT_MODAL.INITIAL_VISIBLE_COUNT, selectedTasks.length))}
                  sx={expandableChipStyles}
                />
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={onClose} 
          variant="outlined"
          disabled={loading}
          className="cancel-button"
        >
          {t('common.cancel')}
        </Button>
        <Button 
          onClick={onConfirm} 
          variant="contained"
          disabled={loading || !selectedTasks || selectedTasks.length === 0}
          className="delete-button"
        >
          {loading ? t('dashboard.bulkActions.deleting') : t('dashboard.bulkActions.deleteModal.deleteButton', { count: selectedTasks.length })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkDeleteModal;
